import {
  type MouseEvent,
  type PointerEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";

/**
 * Hold-to-complete button.
 *
 * Tap-and-hold interaction with a progress-ring fill, ripple animation,
 * audio ticks while filling, and a particle burst + chime on completion.
 * Releasing before the ring fills rewinds the progress without firing
 * `onComplete` - the action only fires once the animation reaches 100%.
 *
 * Designed for the microstep checkbox on /plan and /today (also reused
 * anywhere a "confirm a destructive or irreversible action" pattern is
 * called for - cancel-plan, dismiss-microstep, etc.).
 *
 * Sounds are synthesised inline via Web Audio (no asset files). The
 * AudioContext singleton resumes lazily on the first user pointerdown,
 * which is also the only gesture that browsers allow to unblock audio.
 *
 * Behaviour:
 *   - Controlled by `done` for the persistent state.
 *   - The "bursting" particle burst is local-only; it fires when the
 *     user finishes the hold themselves, not when `done` flips externally.
 *   - `onComplete` is awaited so the parent can revert if the network
 *     call fails; the local completed animation stays regardless.
 */

let __audioCtx: AudioContext | null = null;

// App-wide interaction-sound mute, synced by the host app's sound setting so
// the hold ticks and completion chord obey the same Settings toggle as every
// other cue (the per-instance `soundMuted` prop still mutes a single control,
// e.g. an explainer preview). Kept here, not imported from the app, so
// packages/ui stays dependency-free; the web sound store calls this on change.
let __globalMuted = false;
export function setInteractionSoundMuted(muted: boolean): void {
  __globalMuted = muted;
}

function getAudioCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!__audioCtx) {
    const AC =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    __audioCtx = new AC();
  }
  if (__audioCtx.state === "suspended") void __audioCtx.resume();
  return __audioCtx;
}

/**
 * Single short tick. Frequency climbs from 360 -> 880 Hz as the gauge fills,
 * giving the hold a subtle "filling-up" cue. Exported so sibling components
 * (e.g. <HoldToCompletePill>) can reuse the same audio palette.
 */
export function playProgressTick(progress: number) {
  if (__globalMuted) return;
  const ctx = getAudioCtx();
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = "sine";
  osc.frequency.value = 360 + progress * 520;
  const t = ctx.currentTime;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.06, t + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
  osc.start(t);
  osc.stop(t + 0.1);
}

/** Major-7 chord arpeggio (C5-E5-G5-B5, 45ms apart) on completion. */
export function playCompleteSound() {
  if (__globalMuted) return;
  const ctx = getAudioCtx();
  if (!ctx) return;
  [523.25, 659.25, 783.99, 987.77].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.value = freq;
    const t = ctx.currentTime + i * 0.045;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.08, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.42);
    osc.start(t);
    osc.stop(t + 0.45);
  });
}

/**
 * Inject the component's keyframes + non-utility selectors into document.head.
 * Doing this in a useEffect (instead of rendering a <style> tag inside the
 * button, which some browsers strip from inside interactive elements) makes
 * the rule set definitely active. Idempotent via a module-level guard.
 */
const STYLE_ID = "hold-to-complete-styles";
let __stylesInjected = false;
/** Inject the shared keyframes + selectors. Exported for sibling components. */
export function injectHoldToCompleteStyles() {
  if (__stylesInjected || typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) {
    __stylesInjected = true;
    return;
  }
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = HOLD_TO_COMPLETE_CSS;
  document.head.appendChild(style);
  __stylesInjected = true;
}

export interface HoldToCompleteProps {
  done: boolean;
  onComplete: () => void | Promise<void>;
  onUndo?: () => void;
  /** Pixel size of the button (width === height). Default 38. */
  size?: number;
  /** Hold duration in ms before completion fires. Default 900. */
  holdMs?: number;
  disabled?: boolean;
  soundMuted?: boolean;
  ariaLabel?: string;
  className?: string;
  /**
   * Visual-only overrides for demos and explainers. These do not fire
   * callbacks or change the real hold state machine.
   */
  previewProgress?: number;
  previewHolding?: boolean;
  previewDone?: boolean;
  previewBursting?: boolean;
}

const TICK_INTERVAL_MS = 110;
const CANCEL_DECAY_PER_FRAME = 0.06;

// Token references, not literals, so the ring follows the colour theme.
const TEAL = "var(--color-teal-700)";
const TEAL_DEEP = "var(--color-teal-800)";
const TRACK = "var(--color-mist-dark)";

export interface UseHoldToCompleteOptions {
  done: boolean;
  onComplete: () => void | Promise<void>;
  holdMs?: number;
  disabled?: boolean;
  soundMuted?: boolean;
}

export interface UseHoldToCompleteState {
  /** 0..1, drives the ring + any other progress UI. */
  progress: number;
  /** True between pointerdown and pointerup-or-completion. */
  holding: boolean;
  /** Briefly true after completion for the burst animation; consumers usually ignore this. */
  bursting: boolean;
  /** Pointer and keyboard handlers to spread onto the interactive element. */
  bind: {
    onPointerDown: (e: PointerEvent<HTMLElement>) => void;
    onPointerUp: (e: PointerEvent<HTMLElement>) => void;
    onPointerCancel: (e: PointerEvent<HTMLElement>) => void;
    onPointerLeave: (e: PointerEvent<HTMLElement>) => void;
    onLostPointerCapture: (e: PointerEvent<HTMLElement>) => void;
    onContextMenu: (e: MouseEvent<HTMLElement>) => void;
    onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => void;
    onKeyUp: (e: ReactKeyboardEvent<HTMLElement>) => void;
    onBlur: () => void;
  };
}

/**
 * Shared hold-to-complete state machine. Powers both <HoldToComplete> (a
 * circular icon-button) and any custom surface that needs the same hold +
 * audio + completion behaviour (e.g. the labelled pill on AttentionCardLarge).
 *
 * The hook owns the RAF loop, AudioContext lifecycle, and the cancel-decay
 * animation. Consumers render whatever visual they want and bind the
 * returned pointer handlers to their root element.
 */
export function useHoldToComplete(opts: UseHoldToCompleteOptions): UseHoldToCompleteState {
  const { done, onComplete, holdMs = 900, disabled = false, soundMuted = false } = opts;
  const [progress, setProgress] = useState(done ? 1 : 0);
  const [holding, setHolding] = useState(false);
  const [bursting, setBursting] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef(0);
  const lastTickRef = useRef(0);
  const completedRef = useRef(done);
  const capturedPointerRef = useRef<{ target: HTMLElement; pointerId: number } | null>(null);

  useEffect(() => {
    injectHoldToCompleteStyles();
  }, []);

  useEffect(() => {
    completedRef.current = done;
    if (done) {
      setProgress(1);
      return;
    }
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setHolding(false);
    setBursting(false);
    setProgress(0);
  }, [done]);

  const releasePointerCapture = useCallback((event?: PointerEvent<HTMLElement>) => {
    const captured = capturedPointerRef.current;
    const target = captured?.target ?? event?.currentTarget;
    const pointerId = captured?.pointerId ?? event?.pointerId;
    capturedPointerRef.current = null;

    if (!target || pointerId === undefined) return;

    try {
      if (!target.hasPointerCapture || target.hasPointerCapture(pointerId)) {
        target.releasePointerCapture?.(pointerId);
      }
    } catch {
      // The pointer may already be gone by the time React delivers cleanup.
    }
  }, []);

  useEffect(() => {
    if (done) releasePointerCapture();
  }, [done, releasePointerCapture]);

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      releasePointerCapture();
    };
  }, [releasePointerCapture]);

  const fire = useCallback(() => {
    completedRef.current = true;
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    releasePointerCapture();
    setHolding(false);
    setProgress(1);
    setBursting(true);
    if (!soundMuted) playCompleteSound();
    void onComplete();
    window.setTimeout(() => setBursting(false), 900);
  }, [onComplete, releasePointerCapture, soundMuted]);

  const tick = useCallback(() => {
    const t = performance.now() - startRef.current;
    const p = Math.min(1, t / holdMs);
    // flushSync guarantees the new ring position paints this frame.
    // Without it, React 18 may batch the setProgress calls fired inside
    // requestAnimationFrame and only commit once per microtask boundary -
    // visually the ring would jump in large steps instead of sweeping.
    flushSync(() => {
      setProgress(p);
    });
    if (!soundMuted && t - lastTickRef.current > TICK_INTERVAL_MS) {
      lastTickRef.current = t;
      playProgressTick(p);
    }
    if (p >= 1) {
      fire();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [holdMs, soundMuted, fire]);

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      if (disabled || completedRef.current) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.preventDefault();
      try {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        capturedPointerRef.current = { target: e.currentTarget, pointerId: e.pointerId };
      } catch {
        // setPointerCapture can throw if the pointer is no longer active;
        // pointerleave still cancels, so we just continue without capture.
      }
      setHolding(true);
      startRef.current = performance.now();
      lastTickRef.current = 0;
      tick();
    },
    [disabled, tick],
  );

  const cancelHold = useCallback(
    (event?: PointerEvent<HTMLElement>) => {
      releasePointerCapture(event);
      if (completedRef.current) return;
      setHolding(false);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      const decay = () => {
        setProgress((p) => {
          const np = Math.max(0, p - CANCEL_DECAY_PER_FRAME);
          if (np > 0) rafRef.current = requestAnimationFrame(decay);
          return np;
        });
      };
      decay();
    },
    [releasePointerCapture],
  );

  const onPointerLeave = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (capturedPointerRef.current) return;
      cancelHold(event);
    },
    [cancelHold],
  );

  const onContextMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
  }, []);

  /*
   * Hold, by keyboard. The control is a real button, so it takes focus and shows
   * a focus ring, but it used to bind pointer events only: Enter and Space could
   * undo a completed step and never complete one (WCAG 2.1.1). Holding the key
   * runs the same state machine the pointer does, so the gesture, the ring and
   * the audio stay identical.
   */
  const keyHoldingRef = useRef(false);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (!isActivationKey(event.key)) return;
      // Once complete the button means "undo", so the native click must through.
      if (disabled || completedRef.current) return;
      // Suppresses the native click and, for Space, the page scroll.
      event.preventDefault();
      // Held keys auto-repeat; that is one hold, not one per repeat.
      if (keyHoldingRef.current) return;
      keyHoldingRef.current = true;
      setHolding(true);
      startRef.current = performance.now();
      lastTickRef.current = 0;
      tick();
    },
    [disabled, tick],
  );

  const endKeyHold = useCallback(() => {
    if (!keyHoldingRef.current) return;
    keyHoldingRef.current = false;
    cancelHold();
  }, [cancelHold]);

  const onKeyUp = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (!isActivationKey(event.key)) return;
      endKeyHold();
    },
    [endKeyHold],
  );

  // Tabbing away mid-hold must not leave the ring filling on an unfocused control.
  const onBlur = useCallback(() => {
    endKeyHold();
  }, [endKeyHold]);

  return {
    progress,
    holding,
    bursting,
    bind: {
      onPointerDown,
      onPointerUp: cancelHold,
      onPointerCancel: cancelHold,
      onPointerLeave,
      onLostPointerCapture: cancelHold,
      onContextMenu,
      onKeyDown,
      onKeyUp,
      onBlur,
    },
  };
}

/** Enter and Space are the two keys that activate a button. */
function isActivationKey(key: string): boolean {
  return key === "Enter" || key === " " || key === "Spacebar";
}

export function HoldToComplete({
  done,
  onComplete,
  onUndo,
  size = 38,
  holdMs = 900,
  disabled = false,
  soundMuted = false,
  ariaLabel,
  className,
  previewProgress,
  previewHolding,
  previewDone,
  previewBursting,
}: HoldToCompleteProps) {
  const suppressNextUndoClickRef = useRef(false);
  const handleComplete = useCallback(() => {
    suppressNextUndoClickRef.current = true;
    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        suppressNextUndoClickRef.current = false;
      }, 350);
    }
    return onComplete();
  }, [onComplete]);

  const { progress, holding, bursting, bind } = useHoldToComplete({
    done,
    onComplete: handleComplete,
    holdMs,
    disabled,
    soundMuted,
  });
  const visualProgress = Math.max(0, Math.min(1, previewProgress ?? progress));
  const visualHolding = previewHolding ?? holding;
  const visualDone = previewDone ?? done;
  const visualBursting = previewBursting ?? bursting;

  // SVG arc geometry. The ring sits proportionally inside the button bounds
  // so the stroke has room to render without clipping; the visible stroke
  // stays chunky at every size.
  const inset = Math.max(3, Math.round(size * 0.1));
  const r = (size - inset * 2) / 2;
  const circumference = 2 * Math.PI * r;
  const strokeWidth = Math.max(2, Math.round(size * 0.08));

  const label = ariaLabel ?? (done ? "Undo completion" : "Hold to complete");

  return (
    <button
      type="button"
      disabled={disabled}
      // Owns its own Web Audio (filling ticks + completion chord), so it opts
      // out of the design system's default press cue.
      data-silent=""
      {...bind}
      onContextMenu={bind.onContextMenu}
      onClick={() => {
        if (!done) return;
        if (suppressNextUndoClickRef.current) {
          suppressNextUndoClickRef.current = false;
          return;
        }
        onUndo?.();
      }}
      aria-label={label}
      aria-pressed={done}
      className={[
        "hold-to-complete-btn",
        "relative inline-grid place-items-center flex-shrink-0 rounded-full",
        "appearance-none border-0 p-0 bg-transparent cursor-pointer",
        "transition-transform duration-100",
        "select-none touch-manipulation",
        visualHolding ? "is-holding" : "",
        visualDone ? "is-done" : "",
        visualBursting ? "is-bursting" : "",
        disabled ? "opacity-50 cursor-not-allowed" : "",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        width: size,
        height: size,
        WebkitTapHighlightColor: "transparent",
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
      }}
    >
      {/* Three concentric expanding rings while holding */}
      <span className="hb-ripple" aria-hidden="true" style={{ borderColor: TEAL }} />
      <span className="hb-ripple" aria-hidden="true" style={{ borderColor: TEAL }} />
      <span className="hb-ripple" aria-hidden="true" style={{ borderColor: TEAL }} />

      {/* Progress ring. The track is a plain gray circle; the filled arc
       *  is the same circle stroked teal with a constant `strokeDasharray`
       *  (the full circumference) and an animated `strokeDashoffset` that
       *  hides the un-filled portion. Single-number `dashoffset` is reliable
       *  to animate across browsers - the two-value `dasharray` pattern
       *  some browsers refuse to interpolate.
       *
       *  Z-index keeps the SVG above the inner `.hb-fill` so the swelling
       *  centre disc doesn't obscure the ring at high progress. */}
      <svg
        className="hb-ring pointer-events-none"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ position: "absolute", inset: 0, zIndex: 1 }}
        aria-hidden="true"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={TRACK}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={TEAL}
          strokeWidth={strokeWidth + 0.5}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - visualProgress)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>

      {/* Inner fill that swells with progress. Capped at 0.7 opacity while
       *  holding so the SVG ring on top stays readable - only when done
       *  flips true (the `.is-done` CSS rule) does the fill go fully opaque. */}
      <span
        className="hb-fill"
        style={{
          transform: `scale(${0.4 + visualProgress * 0.6})`,
          opacity: visualDone ? 1 : 0.15 + visualProgress * 0.55,
          background: `radial-gradient(circle, ${TEAL} 0%, ${TEAL_DEEP} 70%)`,
        }}
      />

      {/* Check icon - hidden until complete, then springs in */}
      <span className="hb-check">
        <svg
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.4}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-full w-full"
          aria-hidden="true"
        >
          <path d="M4 10.5 L8.5 15 L16 6.5" />
        </svg>
      </span>

      {/* Particle burst on completion */}
      {visualBursting && (
        <span className="hb-burst pointer-events-none absolute inset-0" aria-hidden="true">
          {Array.from({ length: 10 }).map((_, i) => {
            const angle = (i / 10) * Math.PI * 2;
            const dx = Math.cos(angle) * (size * 0.7);
            const dy = Math.sin(angle) * (size * 0.7);
            return (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: 10 fixed particles in a stable layout
                key={i}
                className="hb-particle"
                style={
                  {
                    background: TEAL,
                    "--dx": `${dx}px`,
                    "--dy": `${dy}px`,
                    animationDelay: `${i * 0.012}s`,
                  } as React.CSSProperties
                }
              />
            );
          })}
        </span>
      )}
    </button>
  );
}

const HOLD_TO_COMPLETE_CSS = `
.hold-to-complete-btn:hover:not(.is-holding):not(.is-done):not(:disabled) { transform: scale(1.05); }
.hold-to-complete-btn:active:not(:disabled) { transform: scale(0.96); }

.hold-to-complete-btn .hb-fill {
  position: absolute;
  inset: 14%;
  border-radius: 50%;
  opacity: 0;
  transform: scale(0.4);
  transition: transform .08s ease-out, opacity .08s ease-out;
  pointer-events: none;
}
.hold-to-complete-btn.is-done .hb-fill {
  opacity: 1 !important;
  transform: scale(1) !important;
  transition: transform .35s cubic-bezier(.34,1.56,.64,1), opacity .2s ease;
}

.hold-to-complete-btn .hb-check {
  position: relative; z-index: 2;
  color: var(--color-ink-on-brand);
  width: 56%; height: 56%;
  display: inline-flex; align-items: center; justify-content: center;
  transform: scale(0);
  opacity: 0;
  transition: transform .35s cubic-bezier(.34, 1.56, .64, 1), opacity .15s ease;
}
.hold-to-complete-btn.is-done .hb-check {
  transform: scale(1);
  opacity: 1;
}

@keyframes hold-to-complete-ripple {
  0%   { transform: scale(1);   opacity: 0.55; }
  100% { transform: scale(2.6); opacity: 0;    }
}
.hold-to-complete-btn .hb-ripple {
  position: absolute; inset: 0;
  border-radius: 50%;
  border-style: solid;
  border-width: 1.5px;
  opacity: 0;
  pointer-events: none;
}
.hold-to-complete-btn.is-holding .hb-ripple {
  animation: hold-to-complete-ripple 1.1s ease-out infinite;
}
.hold-to-complete-btn.is-holding .hb-ripple:nth-of-type(2) { animation-delay: 0.36s; }
.hold-to-complete-btn.is-holding .hb-ripple:nth-of-type(3) { animation-delay: 0.72s; }

@keyframes hold-to-complete-particle {
  0%   { transform: translate(0,0) scale(1);   opacity: 1; }
  70%  { opacity: 1; }
  100% { transform: translate(var(--dx), var(--dy)) scale(0.3); opacity: 0; }
}
.hold-to-complete-btn .hb-particle {
  position: absolute;
  top: 50%; left: 50%;
  width: 6px; height: 6px;
  margin-left: -3px; margin-top: -3px;
  border-radius: 50%;
  animation: hold-to-complete-particle 0.85s cubic-bezier(.18, .8, .35, 1) forwards;
}

/*
 * The rehearsal. A first-time member has no way to learn that completion is
 * gated on a hold: the ring only moves once you already know to hold it, so
 * tapping does nothing and the card looks broken.
 *
 * Rather than point at the control with new furniture, the control performs
 * the gesture. This is the same circle, the same teal, and the same 900ms at
 * the same linear rate as the real hold, so it rehearses the gesture instead
 * of describing it. Linear is required: an eased demo would teach a hold
 * that speeds up, and the real one does not.
 *
 * Timings across the 2100ms loop: fill 0-900ms, rest full to 1150ms, release
 * by 1350ms, then wait. Release is fast where the fill is slow, because the
 * member is deciding through the hold and the system is only responding on
 * the way back.
 */
@keyframes hold-to-complete-hint {
  0%   { stroke-dashoffset: var(--hint-c); opacity: 0; animation-timing-function: linear; }
  3%   { stroke-dashoffset: var(--hint-c); opacity: 1; animation-timing-function: linear; }
  43%  { stroke-dashoffset: 0; opacity: 1; animation-timing-function: linear; }
  55%  { stroke-dashoffset: 0; opacity: 1; animation-timing-function: cubic-bezier(0.23, 1, 0.32, 1); }
  64%  { stroke-dashoffset: var(--hint-c); opacity: 0.9; animation-timing-function: linear; }
  76%  { stroke-dashoffset: var(--hint-c); opacity: 0; }
  100% { stroke-dashoffset: var(--hint-c); opacity: 0; }
}
.hold-hint-arc { animation: hold-to-complete-hint 2100ms linear infinite; }
/* Belt and braces for consumers that keep the arc mounted through a hold: the
   live stroke is the answer once the gesture starts, and two arcs on one
   circle read as a glitch. */
.is-holding .hold-hint-arc,
.is-done .hold-hint-arc { animation: none; opacity: 0; }

@media (prefers-reduced-motion: reduce) {
  /* The word "Hold" carries the instruction on its own. Movement goes, the
     comprehension aid stays. */
  .hold-hint-arc { animation: none; opacity: 0; }
}

@keyframes hold-to-complete-shock {
  0%   { transform: scale(0.6); opacity: 0.6; }
  100% { transform: scale(2.8); opacity: 0;   }
}
.hold-to-complete-btn.is-bursting::after {
  content: "";
  position: absolute; inset: 0;
  border-radius: 50%;
  border: 2px solid var(--color-teal-700);
  animation: hold-to-complete-shock 0.7s ease-out forwards;
  pointer-events: none;
}
`;
